import { FieldArray, Form, Formik } from "formik";
import { useCompanySuggestion } from "pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Suppliers/hooks/useCompanySuggestion";
import { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { FieldForm } from "components/RFQ/Input";
import { Flex, Spinner } from "@chakra-ui/react";
import { cn, showToast } from "utils/helpers";
import { Icon } from "assets/Icon";
import { Button } from "newui/components/Button";
import { CompanySuggestionContent } from "pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Suppliers/components/CompanySuggestionContent";
import { TextArea } from "components/FormInputs/TextArea";
import { useMutation } from "@tanstack/react-query";
import { postAttachSuppliersToSupplierList } from "actions/RFQ/supplierListsActions";
import apiV2 from "api-v2";
import { Checkbox } from "components/RFQ/Checkbox";

export const GuestSelector = ({
	additionalMessage,
	handleAdditionalMessageChange,
	handleSubmit,
	inviteSupplierHandler,
	onClose,
	rfqId,
	supplierListUuid,
}) => {
	const [currentFieldIndex, setCurrentFieldIndex] = useState(null);
	const [isAttachSuccess, setIsAttachSuccess] = useState(false);
	const [isChecked, setIsChecked] = useState(false);
	const [shouldFocus, setShouldFocus] = useState(null);
	// eslint-disable-next-line no-unused-vars
	const [suggestionStates, setSuggestionStates] = useState({});
	const nameFieldRefs = useRef({});

	const initialValues = {
		fieldData: [{ name: "", email: "", supplier_id: null }],
		additional_message: "",
	};

	// Handle focus after render
	useEffect(() => {
		if (shouldFocus !== null) {
			const input = nameFieldRefs.current[shouldFocus];
			if (input) {
				input.focus();
			}
			setShouldFocus(null);
		}
	}, [shouldFocus]);

	const validationSchema = yup.object().shape({
		fieldData: yup.array().of(
			yup.object().shape({
				name: yup.string().required("Name is required"),
				email: yup
					.string()
					.email("Invalid email")
					.required("Email is required"),
			})
		),
		additional_message: yup.string(),
	});

	const {
		companyData,
		debouncedEmailCheck,
		isVisible: showSuggestion,
		handleClose: handleSuggestionClose,
		setIsVisible,
		isLoading,
	} = useCompanySuggestion({
		onValidData: () => {},
		variant: "inline",
	});

	const handleSupplierAttached = (
		index,
		data,
		setFieldValue,
		setFieldTouched
	) => {
		if (!data?.company) {
			return;
		}

		// update form fields with company data
		const name = data.company.name || data.company.contact_name;
		const email = data.company.email;
		const supplier_id = data.company.id;

		setFieldValue(`fieldData.${index}.name`, name);
		setFieldValue(`fieldData.${index}.email`, email);
		setFieldValue(`fieldData.${index}.supplier_id`, supplier_id); // Store supplier_id
		setFieldTouched(`fieldData.${index}.name`, true, false);
		setFieldTouched(`fieldData.${index}.email`, true, false);
	};

	const { mutate: inviteSupplier } = useMutation({
		mutationFn: async (payload) => {
			const res = await apiV2.post(`rfq/${rfqId}/invite-supplier`, payload);
			return res.data;
		},
		onSuccess: (data) => {
			showToast(data?.message, data?.title, true);
			inviteSupplierHandler?.(data.data?.supplierInvited);
			onClose();
		},
		onError: (error) => {
			showToast(error?.response?.data?.message, "Error");
		},
	});

	const { mutate: attachSupplier, isLoading: attachLoading } = useMutation({
		mutationFn: async (payload) => {
			const response = await postAttachSuppliersToSupplierList(
				supplierListUuid,
				payload.selectedIds
			);
			// if response is null or undefined, throw an error to trigger onError
			if (!response) {
				throw new Error("Failed to attach supplier");
			}

			return response;
		},
		onSuccess: (data, variables) => {
			if (data?.status === true) {
				setIsAttachSuccess(true);
				handleSupplierAttached(
					currentFieldIndex,
					{
						company: variables.selectedCompany,
						attachResponse: data,
						fromAddToList: true,
					},
					variables.formikHelpers.setFieldValue,
					variables.formikHelpers.setFieldTouched,
					variables.formikHelpers.values,
					inviteSupplier
				);
			}
		},
		onError: (error, variables) => {
			// Reset the form fields for the current index
			variables.formikHelpers.setFieldValue(
				`fieldData.${currentFieldIndex}.name`,
				""
			);
			variables.formikHelpers.setFieldValue(
				`fieldData.${currentFieldIndex}.email`,
				""
			);
			variables.formikHelpers.setFieldValue(
				`fieldData.${currentFieldIndex}.supplier_id`,
				null
			);

			// Close suggestion content
			setIsVisible(false);
			setCurrentFieldIndex(null);

			if (error?.response?.data?.message) {
				showToast(error.response.data.message, "Error");
			}
		},
	});

	const handleAddToList = (selectedIds, selectedCompany, formikHelpers) => {
		attachSupplier({
			selectedIds,
			selectedCompany,
			formikHelpers,
		});
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
		>
			{({
				values,
				isValid,
				dirty,
				isSubmitting,
				setFieldValue,
				setFieldTouched,
			}) => {
				const handleEmailChange = (e, index) => {
					const newValue = e.target.value;

					setFieldValue(`fieldData.${index}.email`, newValue);
					setCurrentFieldIndex(index);

					if (e.nativeEvent?.inputType === "insertFromPaste") {
						debouncedEmailCheck.cancel();
						debouncedEmailCheck(newValue);
					} else {
						debouncedEmailCheck(newValue);
					}
				};

				const handleRemoveField = (index, remove) => {
					// Remove the suggestion state for this index
					setSuggestionStates((prev) => {
						const newStates = { ...prev };
						delete newStates[index];
						// Shift remaining states up
						Object.keys(newStates).forEach((key) => {
							if (parseInt(key) > index) {
								newStates[parseInt(key) - 1] = newStates[key];
								delete newStates[key];
							}
						});
						return newStates;
					});

					if (currentFieldIndex === index) {
						setCurrentFieldIndex(null);
					} else if (currentFieldIndex > index) {
						setCurrentFieldIndex((prev) => prev - 1);
					}
					remove(index);
				};

				const handleAddField = (push) => {
					const newIndex = values.fieldData.length;
					push({ name: "", email: "", supplier_id: null });
					setShouldFocus(newIndex);
				};

				return (
					<Form>
						<FieldArray name="fieldData">
							{({ push, remove }) => (
								<div>
									{values.fieldData.map((fd, index) => (
										<div key={index}>
											<div className="mb-20 rounded-md bg-utility-blue-light-100 p-6">
												<h3 className="text-xl/5 font-bold">
													Guest {index + 1}
												</h3>
												<div className="group relative mb-5 mt-5 flex items-start pr-8">
													<div
														style={{ flex: 1 }}
														className="mr-3"
													>
														<FieldForm
															ref={(el) => {
																if (el) {
																	const input = el.querySelector("input");
																	if (input) {
																		nameFieldRefs.current[index] = input;
																	}
																}
															}}
															label="Name"
															type="text"
															placeholder="Name"
															name={`fieldData.${index}.name`}
														/>
													</div>
													<div
														style={{ flex: 1 }}
														className="relative ml-3"
													>
														<FieldForm
															label="Email"
															type="text"
															placeholder="you@example.com"
															name={`fieldData.${index}.email`}
															onChange={(e) => handleEmailChange(e, index)}
														/>
														{isLoading &&
															currentFieldIndex === index &&
															!showSuggestion && (
																<div className="absolute bottom-5 right-5">
																	<Spinner
																		size="sm"
																		thickness="2px"
																		speed="0.65s"
																	/>
																</div>
															)}
													</div>
													{values.fieldData.length !== 1 && (
														<div
															className={cn(
																"absolute -right-4 top-10 hidden h-max w-max cursor-pointer rounded-full bg-gray-300 p-2 hover:bg-red-200 group-hover:flex"
															)}
															onClick={() => handleRemoveField(index, remove)}
														>
															<Icon
																icon="trash"
																style={{
																	width: "20px",
																	height: "20px",
																}}
															/>
														</div>
													)}
												</div>

												{index === values.fieldData.length - 1 && (
													<Flex
														gap={4}
														align={"center"}
													>
														<Button
															btntype="secondary"
															type={"button"}
															className="!w-max"
															onClick={() => handleAddField(push)}
															disabled={
																showSuggestion &&
																!isAttachSuccess &&
																currentFieldIndex !== null
															}
														>
															<Flex
																gap={2}
																className="items-center"
															>
																<Icon icon="plus-blue" />
																Add another
															</Flex>
														</Button>
													</Flex>
												)}

												{showSuggestion && currentFieldIndex === index && (
													<div className="mt-4">
														{isAttachSuccess ? (
															<div className="mr-8 flex items-center gap-6 rounded-[8px] border-2 border-royal-blue bg-white p-6">
																<p className="font-medium text-utility-brand-700">
																	Supplier Added successfully. To invite them,
																	please complete this invite form.
																</p>
																<Button
																	btntype="base"
																	size="sm"
																	onClick={() => {
																		setIsAttachSuccess(false);
																		handleSuggestionClose();
																	}}
																	type="button"
																>
																	Close
																</Button>
															</div>
														) : (
															<CompanySuggestionContent
																companyData={companyData}
																variant="inline"
																className="mr-8 rounded-[8px] border-2 border-royal-blue bg-white p-6"
																onClose={() => {
																	setSuggestionStates((prev) => ({
																		...prev,
																		[index]: false,
																	}));
																	handleSuggestionClose();
																}}
																currentFieldIndex={index}
																onAddToList={(selectedIds, selectedCompany) =>
																	handleAddToList(
																		selectedIds,
																		selectedCompany,
																		{
																			setFieldValue,
																			setFieldTouched,
																			values,
																		}
																	)
																}
																onCancel={handleSuggestionClose}
																isLoading={attachLoading}
																hasSupplierList={supplierListUuid !== undefined}
															/>
														)}
													</div>
												)}
											</div>
										</div>
									))}
								</div>
							)}
						</FieldArray>
						<div className="">
							<TextArea
								value={additionalMessage}
								label="Additional Message"
								name="additional_message"
								placeholder="Additional Message"
								className="flex flex-col"
								errorClassName="!mt-1"
								onChange={handleAdditionalMessageChange}
							/>
						</div>
						<div
							className="mb-5 mt-2 flex cursor-pointer items-center justify-start"
							onClick={() => {
								setIsChecked(!isChecked);
							}}
						>
							<div className="w-30">
								<Checkbox
									checked={isChecked}
									onChange={() => {
										setIsChecked(!isChecked);
									}}
								/>
							</div>
							<div
								className="ml-3 flex-1 font-roboto font-medium text-gray-700"
								style={{ fontSize: "14px" }}
							>
								I've verified the above details and all email addresses are
								correct.
							</div>
						</div>
						<Button
							type="submit"
							style={{ width: "100%" }}
							disabled={!isChecked || !isValid || !dirty || isSubmitting}
						>
							{isSubmitting ? <Spinner></Spinner> : "Invite"}
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};
